import * as vp from 'vopay';
import { FunctionComponent, JSX, useEffect } from 'react';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';

const { Table, Box, SubTitle, Badge, formattedCurrency } = vp;

type TransactionInfoProps = {
  transaction: vp.InboundETransferList | undefined;
};

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
};

type FC = FunctionComponent<TransactionInfoProps>;

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type TablePropsHandlers = {
  transactionDetail: () => vp.TableProps;
  errorInfo: () => vp.TableProps;
};

const InboundETransferTransactionInfo: FC = () => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.InboundETransferTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const { state } = useLocation<DataProps>();
  const currentDate = moment().utc().format('YYYY-MM-DD');
  const { transactions, fetchTransactionsHistory } = useFetchTransactionsHistory();

  useEffect(() => {
    fetchTransactionsHistory({
      StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
      EndDateTime: currentDate,
      TransactionID: state.transactionId,
    });
  }, [state.transactionId, currentDate]);

  return (
    <>
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('transactionDetailsTitle') || ''} />
      </Box>
      <Table {...getTableProps('transactionDetail')} />
      {state?.hasErrorInfo && (
        <>
          <Box mt="lg" pt="md" mb="md">
            <SubTitle subTitle={t('errorInfoTitle') || ''} />
          </Box>
          <Table {...getTableProps('errorInfo')} />
        </>
      )}
    </>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      transactionDetail: () => {
        const transactionDetailsPairs: KeyValuePair[] = [
          {
            display: t('transactionDetails.transactionID'),
            value: transactions.map((data) => data.TransactionID).pop() || '',
            visible: true,
          },
          {
            display: t('transactionDetails.transactionStatus'),
            value: (
              <Badge
                label={vp.capitalizeFirstLetter(
                  tGlobal(
                    `Transaction.TransactionHistory.statuses.${
                      transactions.map((data) => data.TransactionStatus).pop() || ''
                    }`,
                    { defaultValue: transactions.map((data) => data.TransactionStatus).pop() },
                  ),
                )}
                hasIcon
                color={vp.GetColorStatus(transactions.map((data) => data.TransactionStatus).pop() || '')}
              />
            ),
            visible: true,
          },
          {
            display: t('transactionDetails.dateReceived'),
            value: transactions.map((data) => data.TransactionDateTime).pop() || '',
            visible: true,
          },
          {
            display: t('transactionDetails.type'),
            value: t('transactionDetails.inboundETransfer') || '',
            visible: true,
          },
          {
            display: t('transactionDetails.amount'),
            value: `$${formattedCurrency(transactions.map((data) => data.CreditAmount).pop() || '')}`,
            visible: true,
          },
          {
            display: t('transactionDetails.currency'),
            value: transactions.map((data) => data.Currency).pop() || '',
            visible: true,
          },
          {
            display: t('transactionDetails.name'),
            value: transactions.map((data) => data.FullName).pop() || '',
            visible: true,
          },
          {
            display: t('transactionDetails.message'),
            value: transactions.map((data) => data.Notes).pop() || '',
            visible: !!transactions.map((data) => data.Notes).pop(),
          },
        ];

        const transactionDetailsProps: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('transactionDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: transactionDetailsPairs
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return transactionDetailsProps;
      },
      errorInfo: () => {
        const ErrorInfoPairs: KeyValuePair[] = [
          {
            display: t('errorInfo.failureDateTime'),
            value: transactions.map((data) => data.LastModified).pop() || '',
          },
          {
            display: t('errorInfo.amount'),
            value: `$${formattedCurrency(transactions.map((data) => data.DebitAmount).pop() || '')}`,
          },
          {
            display: t('errorInfo.failureReason'),
            value: state.failureReason,
          },
        ];

        const ErrorInfoProps: vp.TableProps = {
          name: 'error-info',
          variant: 'pair',
          columns: {
            detailKey: { header: t('errorInfo.header') || '' },
            detailValue: { header: '' },
          },
          rows: ErrorInfoPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return ErrorInfoProps;
      },
    };

    return handlers[handler]();
  }
};

export default InboundETransferTransactionInfo;
